import type { Editor } from "@tiptap/core";
import { Extension } from "@tiptap/core";
import { Plugin } from "@tiptap/pm/state";
import type { DoclistTreeItem } from "~/api/model";

export const REPORT_REFERENCE_COMMAND_TRIGGER = "/";
export const REPORT_REFERENCE_COMMAND_NAME = "ref";
export const REPORT_QUOTE_COMMAND_NAME = "quote";
export const REPORT_HEADING_COMMAND_NAME = "heading";
export const REPORT_IMAGE_COMMAND_NAME = "image";
export const REPORT_HEADING_LEVEL = 2;
export const REPORT_BULLET_LIST_COMMAND_NAME = "bullet";
export const REPORT_NUMBERED_LIST_COMMAND_NAME = "numbered";
const REPORT_SLASH_COMMAND_NAMES = [
  REPORT_REFERENCE_COMMAND_NAME,
  REPORT_QUOTE_COMMAND_NAME,
  REPORT_HEADING_COMMAND_NAME,
  REPORT_IMAGE_COMMAND_NAME,
  REPORT_BULLET_LIST_COMMAND_NAME,
  REPORT_NUMBERED_LIST_COMMAND_NAME,
];
const TRAILING_WHITESPACE_PATTERN = /\s+$/;
const FALLBACK_PAGE_NUMBER_OFFSET = 1;

export type ReportReferencePageOption = {
  pageId: string;
  sectionId: string;
  label: string;
  searchText: string;
  batesNumber?: string | null;
};

export type ReportReferenceDocumentOption = {
  sectionId: string;
  title: string;
  categoryName: string;
  searchText: string;
  pages: ReportReferencePageOption[];
};

type CreateReferenceHtmlParams = {
  sectionId: string;
  pageId: string;
  label: string;
  batesNumber?: string | null;
};

type ReportReferenceSlashCommandOptions = {
  onOpen: (editor: Editor) => void;
};

type BuildReportReferenceDocumentsOptions = {
  formatPageLabel?: (pageNumber: number) => string;
};

export function buildReportReferenceDocuments(
  docList?: { tree?: DoclistTreeItem[] } | null,
  options: BuildReportReferenceDocumentsOptions = {}
): ReportReferenceDocumentOption[] {
  if (!docList?.tree) return [];

  return docList.tree.flatMap((category) =>
    category.sections.flatMap((section) => {
      const sectionPages = section.pages;
      if (!section.name.trim() || !sectionPages || sectionPages.length === 0) {
        return [];
      }

      const pages = sectionPages.map((page, index) => {
        const pageNumber =
          page.originPageNum || index + FALLBACK_PAGE_NUMBER_OFFSET;
        const label = page.batesNumber
          ? page.batesNumber
          : (options.formatPageLabel?.(pageNumber) ?? `Page ${pageNumber}`);
        return {
          pageId: page.id,
          sectionId: section.id,
          label,
          searchText: `${label} ${pageNumber}`.toLowerCase(),
          batesNumber: page.batesNumber,
        };
      });

      return [
        {
          sectionId: section.id,
          title: section.name,
          categoryName: category.category.name,
          searchText: `${section.name} ${category.category.name}`.toLowerCase(),
          pages,
        },
      ];
    })
  );
}

export function createReportReferenceHtml({
  sectionId,
  pageId,
  label,
  batesNumber,
}: CreateReferenceHtmlParams): string {
  const batesRangeAttribute = batesNumber
    ? ` data-bates-range="${escapeHtmlAttribute(batesNumber)}"`
    : "";
  return `<a class="source-map-link" data-section-id="${escapeHtmlAttribute(sectionId)}" data-page-id="${escapeHtmlAttribute(pageId)}"${batesRangeAttribute} href="#" role="button">${escapeHtml(label)}</a>`;
}

export function getReportSlashCommandRange(editor: Editor): {
  from: number;
  to: number;
} | null {
  const { state } = editor;
  const { $from } = state.selection;

  let commandStart: number | null = null;
  for (let offset = $from.parentOffset - 1; offset >= 0; offset -= 1) {
    if (
      $from.parent.textBetween(offset, offset + 1) ===
      REPORT_REFERENCE_COMMAND_TRIGGER
    ) {
      commandStart = offset;
      break;
    }
  }

  if (commandStart === null) return null;

  const commandText = $from.parent
    .textBetween(
      commandStart + REPORT_REFERENCE_COMMAND_TRIGGER.length,
      $from.parentOffset
    )
    .toLowerCase();
  if (
    !REPORT_SLASH_COMMAND_NAMES.some((commandName) =>
      commandName.startsWith(commandText)
    )
  ) {
    return null;
  }

  const textBeforeCommand = $from.parent.textBetween(0, commandStart);
  const characterBeforeCommand = textBeforeCommand.at(-1);
  if (characterBeforeCommand && !/\s/.test(characterBeforeCommand)) return null;

  return {
    from: $from.start() + commandStart,
    to: $from.pos,
  };
}

export function insertReportReference(
  editor: Editor,
  page: ReportReferencePageOption
): void {
  const range = getReportSlashCommandRange(editor);
  const referenceHtml = createReportReferenceHtml({
    sectionId: page.sectionId,
    pageId: page.pageId,
    label: page.label,
    batesNumber: page.batesNumber,
  });

  if (range) {
    editor
      .chain()
      .focus()
      .deleteRange(range)
      .insertContent(referenceHtml)
      .insertContent(" ")
      .run();
    return;
  }

  editor.chain().focus().insertContent(referenceHtml).insertContent(" ").run();
}

export function convertReportBlockToQuote(editor: Editor): void {
  const range = getReportSlashCommandRange(editor);

  if (range) {
    const $commandStart = editor.state.doc.resolve(range.from);
    const textBeforeCommand = $commandStart.parent.textBetween(
      0,
      $commandStart.parentOffset
    );
    const trailingWhitespaceLength =
      textBeforeCommand.match(TRAILING_WHITESPACE_PATTERN)?.[0].length ?? 0;
    const deleteFrom = range.from - trailingWhitespaceLength;

    editor
      .chain()
      .focus()
      .deleteRange({
        from: deleteFrom,
        to: range.to,
      })
      .setBlockquote()
      .run();
    return;
  }

  editor.chain().focus().setBlockquote().run();
}

export function removeReportSlashCommand(editor: Editor): boolean {
  const range = getReportSlashCommandRange(editor);
  if (!range) return false;

  editor.chain().focus().deleteRange(range).run();
  return true;
}

export function applyReportHeadingCommand(editor: Editor): void {
  const range = getReportSlashCommandRange(editor);
  const chain = editor.chain().focus();

  if (range) {
    chain.deleteRange(range);
  }

  chain.setHeading({ level: REPORT_HEADING_LEVEL }).run();
}

export function toggleReportBulletList(editor: Editor): void {
  toggleReportList(editor, "bullet");
}

export function toggleReportNumberedList(editor: Editor): void {
  toggleReportList(editor, "numbered");
}

export function isReportSlashCommandActive(editor: Editor): boolean {
  return getReportSlashCommandRange(editor) !== null;
}

export const ReportReferenceSlashCommand =
  Extension.create<ReportReferenceSlashCommandOptions>({
    name: "reportReferenceSlashCommand",

    addProseMirrorPlugins() {
      return [
        new Plugin({
          props: {
            handleTextInput: (_view, _from, _to, text) => {
              if (text !== REPORT_REFERENCE_COMMAND_TRIGGER) return false;

              queueMicrotask(() => {
                if (isReportSlashCommandActive(this.editor)) {
                  this.options.onOpen(this.editor);
                }
              });

              return false;
            },
          },
        }),
      ];
    },
  });

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeHtmlAttribute(value: string): string {
  return escapeHtml(value).replace(/"/g, "&quot;");
}

function toggleReportList(
  editor: Editor,
  listType: "bullet" | "numbered"
): void {
  const range = getReportSlashCommandRange(editor);
  if (!range) return;

  const commandChain = editor.chain().focus().deleteRange(range);
  if (listType === "bullet") {
    commandChain.toggleBulletList().run();
    return;
  }

  commandChain.toggleOrderedList().run();
}
